<script>
  import { onMount } from 'svelte';
  import { format } from 'svelte-i18n';
  import { useNavigate, useParams } from 'svelte-navigator';
  import { Button, ButtonGroup, Icon as BsIcon, Label, Nav, Tooltip } from '@sveltestrap/sveltestrap';

  import { Icon } from '@client/components/icon';
  import { Loader } from '@client/components/loader';
  import { getNotificationsContext } from '@client/components/notifications';
  import { PageHeader } from '@client/components/page';
  import { QRCode } from '@client/components/qr-code';
  import { UserSelect } from '@client/components/user-select';

  import { Tab } from '@client/pages/brandkit/components';
  import { parseImage, parseQrCode } from '@client/pages/brandkit/utils';

  import { userStore } from '@client/stores/user';
  import {
    id,
    isValid,
    loadBackground,
    loading,
    loadTemplate,
    previewType,
    saveBackground,
    saving,
    selectedUser,
    setSelectedUser,
    template,
    updateBackground,
    updatedRulename,
    url
  } from '@client/stores/virtual-background';

  import { Styles } from './background';
  import BackgroundEditor from './background-editor.svelte';
  import { Fields } from './fields';
  import Images from './images.svelte';
  import QRCodes from './qr-codes.svelte';
  import Rulename from './rulename.svelte';

  const { addNotification } = getNotificationsContext();
  const navigate = useNavigate();
  const params = useParams();

  /** @type {boolean} */
  export let edit = false;

  let dimensions = [1920, 1080];
  let userSelectValue = null;
  let invalidRulename = false;
  /** @type {any[]} */
  let users = [];
  let activeSidebar = 'styles';
  let originalRulename = '';
  let showUser = false;

  /** @type {HTMLElement} */
  let drag;
  /** @type {any} */
  let draggedData;
  /** @type {any} */
  let draggedItem = null;
  /** @type {number | null} */
  let draggedFromCell = null;
  /** @type {number | null} */
  let draggedLineNumber = null;
  /** @type {{ cellIndex: number; lineNumber: number; insertAfter: boolean } | null} */
  let dropTarget = null;

  onMount(async () => {
    if ($params.id) {
      await loadBackground($params.id);
    } else {
      await loadTemplate($params.template);
    }

    // Sync initial values after store loads
    originalRulename = $id;
    $updatedRulename = $id;
  });

  const onSave = async () => {
    const success = edit ? await updateBackground() : await saveBackground();

    if (success) {
      addNotification({
        text: $format('label.SUCCESS.BACKGROUND_UPDATE'),
        type: 'success',
        autohide: 2500,
        position: 'top-right'
      });
      navigate('/brandkit/studio');
    } else {
      addNotification({
        text: $format('label.FAILURE.BACKGROUND_UPDATE'),
        type: 'error',
        dismissable: true,
        autohide: 2500,
        position: 'top-right'
      });
    }
  };

  $: isDisabled = !$isValid || $saving || invalidRulename;
  $: padding = $previewType === 'teams' ? [50, 100] : [0, 0];
  $: showLoader = $loading || $saving;

  // User Selection Logic
  $: if (users.length) {
    const userInDomain = users.some((user) => user.value === $selectedUser?.value);
    if (!userInDomain) {
      setSelectedUser(null);
      userSelectValue = null;
    }
    if (!userSelectValue) {
      if (!$selectedUser) {
        const userToSelect = $userStore.isManaging ? users[0] : $userStore.currentUser;
        setSelectedUser(userToSelect);
      }
      userSelectValue = $selectedUser;
    }
  }

  // Drag & Drop Handlers
  /**
   * @param {DragEvent} event
   * @param {any} item
   * @param {number | null} fromCell
   * @param {number | null} fromLine
   */
  const handleDragStart = (event, item, fromCell, fromLine) => {
    draggedItem = item;
    draggedFromCell = fromCell ?? null;
    draggedLineNumber = fromLine ?? null;

    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.dropEffect = 'move';
      if (drag) {
        draggedData = item;
        event.dataTransfer.setDragImage(drag, 20, 20);
      }
    }
  };

  /** @param {DragEvent} event */
  const handleDragOver = (event) => {
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
  };

  /**
   * @param {number} cellIndex
   * @param {number} lineNumber
   * @param {boolean} insertAfter
   */
  const handleDragOverItem = (cellIndex, lineNumber, insertAfter) => {
    dropTarget = { cellIndex, lineNumber, insertAfter };
  };

  const handleDragEnd = () => {
    dropTarget = null;
  };

  /**
   * @param {DragEvent} event
   * @param {number} index
   */
  const handleDrop = (event, index) => {
    event.preventDefault();
    if (draggedItem) {
      // Create a shallow copy of the grid to maintain reactivity
      const newCells = [...$template.cells];
      const items = [...newCells[index]];

      if (draggedFromCell !== null) {
        if (draggedFromCell === index) {
          items.splice(draggedLineNumber, 1);
        } else {
          newCells[draggedFromCell] = newCells[draggedFromCell].filter((i) => i !== draggedItem);
        }
      }

      let insertAt = items.length;
      if (dropTarget && dropTarget.cellIndex === index) {
        insertAt = dropTarget.lineNumber;
        if (dropTarget.insertAfter) insertAt++;
        if (draggedFromCell === index && draggedLineNumber < insertAt) insertAt--;
      }

      items.splice(insertAt, 0, draggedItem);
      newCells[index] = items;

      // Update the store
      $template.cells = newCells;

      // Reset D&D state
      draggedItem = null;
      draggedFromCell = null;
      draggedLineNumber = null;
      draggedData = null;
      dropTarget = null;
    }
  };

  /** @param {DragEvent} event */
  const handleDropToAside = (event) => {
    event.preventDefault();
    if (draggedItem && draggedFromCell !== null) {
      $template.cells[draggedFromCell] = $template.cells[draggedFromCell].filter((i) => i !== draggedItem);
      draggedItem = null;
      draggedFromCell = null;
      draggedLineNumber = null;
      dropTarget = null;
    }
  };

  /**
   * @param {number} index
   * @param {number} lineNumber
   */
  const handleDelete = (index, lineNumber) => {
    $template.cells[index].splice(lineNumber, 1);
    $template.cells = $template.cells; // Trigger Svelte's assignment reactivity
  };

  /**
   * @param {number} index
   * @param {number} line
   * @param {string} tag
   */
  const handleScale = (index, line, tag) => {
    $template.cells[index][line] = tag;
    $template.cells = $template.cells;
  };

  /**
   * @param {number} index
   * @param {number} line
   * @param {string} type
   */
  const handleSelect = (index, line, type) => {
    activeSidebar = type;
  };

  /** @param {string} newSize */
  const handleSizeChange = (newSize) => {
    $previewType = newSize;
    const sizes = { zoom: [1920, 1080], teams: [1920, 1080], '2k': [2560, 1440], '4k': [3840, 2160] };
    dimensions = sizes[newSize] || [1920, 1080];
  };
</script>

<div id="background-editor" class="page background-editor">
  <Loader show={showLoader} backdrop={showLoader} />

  <PageHeader
    title={$id}
    paths={[
      [$format('label.EMPLOYEE_BRANDING'), '/brandkit'],
      [$format('label.DESIGNER_STUDIO'), '/brandkit/studio'],
      [$format('label.EDIT_BACKGROUND')]
    ]}
    class="shadow-none"
  >
    <div slot="title">
      <Rulename
        bind:name={$updatedRulename}
        bind:invalid={invalidRulename}
        initialRulename={originalRulename}
        isEditor={edit}
      />
    </div>

    <Button color="secondary" class="ms-auto me-2" disabled={$saving} on:click={() => navigate('/brandkit/studio')}>
      {$format('label.CANCEL')}
    </Button>
    <Button color="primary" disabled={isDisabled} on:click={onSave}>
      {$format(edit ? 'label.UPDATE' : 'label.SAVE')}
    </Button>
  </PageHeader>

  <div class="page-content">
    {#if !$loading}
      {#if $template}
        <div bind:this={drag} class="drag-ghost" style:color={$template.foreground}>
          {#if draggedData}
            {@const [src] = parseImage(draggedData)}
            {@const [qr] = parseQrCode(draggedData)}
            {#if typeof draggedData === 'string' && (src || /^https?:\/\//.test(draggedData))}
              <img src={src || draggedData} alt={draggedData} class="drag-image-preview" />
            {:else if typeof draggedData === 'string' && qr}
              <QRCode data={qr} />
            {:else}
              <h2 class="drag-text-preview">{draggedData}</h2>
            {/if}
          {/if}
        </div>
      {/if}

      <div class="layout border-start">
        <header class="d-flex align-items-center p-2 border-bottom">
          <div class="d-flex align-items-center flex-grow-1 px-2">
            <Label class="mb-0 me-2">
              {$format('label.PREVIEW')}
            </Label>
            <UserSelect
              bind:users
              value={userSelectValue}
              on:change={(e) => setSelectedUser(e.detail)}
              isClearable={false}
              showChevron
            />
            <Button href={$url} id="show_preview" target="_blank" class="ms-2">
              <Icon name="external-link" />
            </Button>
            <Tooltip target="show_preview" placement="bottom"
              >{$format('label.TOOLTIP.PREVIEW_VIRTUAL_BACKGROUND')}</Tooltip
            >
          </div>
          <div class="d-flex align-items-center ms-auto px-2">
            <Label class="mb-0 me-2">{$format('label.SIZE')}</Label>
            <ButtonGroup class="me-2">
              <Button active={$previewType === 'zoom'} id="size_zoom" on:click={() => handleSizeChange('zoom')}>
                {$format('label.BACKGROUND_SIZE_ZOOM')}
              </Button>
              <Tooltip target="size_zoom" placement="bottom">1920 × 1080</Tooltip>
              <Button active={$previewType === 'teams'} id="size_teams" on:click={() => handleSizeChange('teams')}>
                {$format('label.BACKGROUND_SIZE_TEAMS')}
              </Button>
              <Tooltip target="size_teams" placement="bottom">1920 × 1080</Tooltip>
              <Button active={$previewType === '2k'} id="size_2k" on:click={() => handleSizeChange('2k')}>
                {$format('label.BACKGROUND_SIZE_2K')}
              </Button>
              <Tooltip target="size_2k" placement="bottom">2560 × 1440</Tooltip>
              <Button active={$previewType === '4k'} id="size_4k" on:click={() => handleSizeChange('4k')}>
                {$format('label.BACKGROUND_SIZE_4K')}
              </Button>
              <Tooltip target="size_4k" placement="bottom">3840 × 2160</Tooltip>
            </ButtonGroup>
            <Button active={showUser} id="show_silhouette" on:click={() => (showUser = !showUser)}>
              <Icon name="user" />
            </Button>
            <Tooltip target="show_silhouette" placement="bottom"
              >{$format('label.TOOLTIP.PREVIEW_USER_SILHOUETTE')}</Tooltip
            >
          </div>
        </header>

        {#if $template}
          <div class="preview-content">
            <BackgroundEditor
              background={$template.background}
              foreground={$template.foreground}
              cells={$template.cells}
              blur={$template.blur}
              brightness={$template.brightness}
              {showUser}
              user={$selectedUser}
              width={dimensions[0]}
              height={dimensions[1]}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDragOverItem={handleDragOverItem}
              onDragEnd={handleDragEnd}
              onDrop={handleDrop}
              onDelete={handleDelete}
              onScale={handleScale}
              onSelect={handleSelect}
              {dropTarget}
              {padding}
            />
          </div>

          <aside
            id="design"
            role="region"
            class="border-start"
            on:dragover={handleDragOver}
            on:drop={handleDropToAside}
          >
            <header>
              <Nav pills>
                <Tab
                  active={activeSidebar === 'styles'}
                  collapse
                  icon="wallpaper"
                  on:click={() => (activeSidebar = 'styles')}
                >
                  {$format('label.DESIGNER_BACKGROUND')}
                </Tab>
                <Tab
                  active={activeSidebar === 'fields'}
                  collapse
                  icon="server"
                  on:click={() => (activeSidebar = 'fields')}
                >
                  {$format('label.FIELDS')}
                </Tab>
                <Tab
                  active={activeSidebar === 'images'}
                  collapse
                  icon="image"
                  on:click={() => (activeSidebar = 'images')}
                >
                  {$format('label.IMAGES')}
                </Tab>
                <Tab
                  active={activeSidebar === 'qr-codes'}
                  collapse
                  icon="qr-code"
                  on:click={() => (activeSidebar = 'qr-codes')}
                >
                  {$format('label.QR_CODES')}
                </Tab>
              </Nav>
            </header>

            <div>
              <div class="h-100" class:d-none={activeSidebar !== 'styles'}>
                <Styles
                  background={$template.background}
                  foreground={$template.foreground}
                  onDragStart={handleDragStart}
                  onChange={(bg, fg) => {
                    $template.background = bg;
                    $template.foreground = fg;
                  }}
                />
              </div>
              <div class:d-none={activeSidebar !== 'fields'}>
                <Fields
                  active={activeSidebar === 'fields'}
                  bind:foreground={$template.foreground}
                  onDragStart={handleDragStart}
                />
              </div>
              <div class:d-none={activeSidebar !== 'images'}>
                <Images active={activeSidebar === 'images'} onDragStart={handleDragStart} />
              </div>
              <div class:d-none={activeSidebar !== 'qr-codes'}>
                <QRCodes active={activeSidebar === 'qr-codes'} onDragStart={handleDragStart} />
              </div>
            </div>
          </aside>
        {/if}
      </div>
    {/if}
  </div>
</div>

<style lang="scss" src="./designer.scss"></style>
